import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import toast from "react-hot-toast";
import { ImagePlus, Loader2, Smile, Trash2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { storageService } from "@/services/storage.service";
import { LucideByName, PageIcon } from "@/components/programPage/icons";
import { PAGE_ICON_NAMES } from "@/components/programPage/pageIcons";
import type { ProgramPageCta, ProgramPageIcon } from "@/types/programPage";

/** The small form controls the program page editor is built from. */

const TEXTAREA =
  "w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

export function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  maxLength,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  placeholder?: string;
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <Input
        id={id}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  maxLength,
  rows = 3,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  rows?: number;
  placeholder?: string;
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <textarea
        id={id}
        className={TEXTAREA}
        rows={rows}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/** One entry per line — quicker to write a list of perks than a row of inputs. */
export function LinesField({
  label,
  value,
  onChange,
  maxLines,
  placeholder,
}: {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  maxLines: number;
  placeholder?: string;
}) {
  const id = useId();
  // Kept as raw text while typing so a trailing newline is not eaten.
  const [raw, setRaw] = useState(value.join("\n"));
  return (
    <Field label={label} hint="One per line." htmlFor={id}>
      <textarea
        id={id}
        className={TEXTAREA}
        rows={Math.min(6, Math.max(3, value.length + 1))}
        value={raw}
        placeholder={placeholder}
        onChange={(e) => {
          setRaw(e.target.value);
          onChange(
            e.target.value
              .split("\n")
              .map((line) => line.trim().slice(0, 140))
              .filter(Boolean)
              .slice(0, maxLines)
          );
        }}
      />
    </Field>
  );
}

export function ToggleField({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <label htmlFor={id} className="flex flex-col gap-0.5 text-sm">
        {label}
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function SegmentedField<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <Field label={label}>
      <div role="radiogroup" aria-label={label} className="flex rounded-lg border border-border p-0.5">
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={option.value === value}
            onClick={() => onChange(option.value)}
            className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
              option.value === value
                ? "bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </Field>
  );
}

const HEX = /^#[0-9a-f]{6}$/i;

/** The house pattern: a native swatch paired with a hex input. */
export function ColorField({
  label,
  value,
  onChange,
  onClear,
  placeholder,
}: {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  /** When given, the colour is optional and can be reset to "inherit". */
  onClear?: () => void;
  placeholder?: string;
}) {
  const id = useId();
  const [text, setText] = useState(value ?? "");
  // Follow outside changes (a template, a reset) without fighting the typist:
  // a half-typed hex never equals `value`, so only complete ones are replaced.
  useEffect(() => {
    setText((current) =>
      (value ?? "").toLowerCase() === current.toLowerCase() || (current && !HEX.test(current))
        ? current
        : value ?? ""
    );
  }, [value]);

  return (
    <Field label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} swatch`}
          value={value ?? "#000000"}
          onChange={(e) => {
            setText(e.target.value);
            onChange(e.target.value);
          }}
          className="h-9 w-10 shrink-0 cursor-pointer rounded-lg border border-input bg-transparent p-0.5"
        />
        <Input
          id={id}
          value={text}
          placeholder={placeholder ?? (onClear ? "Theme accent" : "#000000")}
          maxLength={7}
          onChange={(e) => {
            const next = e.target.value.trim();
            setText(next);
            if (HEX.test(next)) onChange(next.toLowerCase());
          }}
          className="font-mono"
        />
        {onClear && value && (
          <button
            type="button"
            onClick={() => {
              setText("");
              onClear();
            }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Reset
          </button>
        )}
      </div>
    </Field>
  );
}

async function upload(file: File): Promise<string | null> {
  try {
    return await storageService.uploadImage(file, "program-pages");
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Upload failed");
    return null;
  }
}

export function ImageField({
  label,
  value,
  onChange,
  hint = "PNG, JPEG or WebP, up to 3MB.",
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  hint?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImagePlus className="h-5 w-5 text-muted-foreground" aria-hidden />
          )}
        </div>
        <div className="flex flex-col items-start gap-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {value ? "Replace" : "Upload"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            setBusy(true);
            const url = await upload(file);
            setBusy(false);
            if (url) onChange(url);
          }}
        />
      </div>
    </Field>
  );
}

const QUICK_EMOJI = [
  "🔥", "⚡", "🏆", "👑", "💎", "⭐", "🚀", "🎯", "💰", "🎁", "🌱", "💪",
  "📸", "🎬", "📱", "🛍️", "🤝", "❤️", "✨", "🥇", "🥈", "🥉", "🏋️", "🎉",
];

/** Three ways to say "icon": pick one, type an emoji, or upload your own. */
export function IconField({
  label,
  value,
  onChange,
  lucideOnly = false,
}: {
  label: string;
  value: ProgramPageIcon | null;
  onChange: (value: ProgramPageIcon | null) => void;
  /** The sidebar entry takes a drawn icon only, and always has one. */
  lucideOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ProgramPageIcon["kind"]>(value?.kind ?? "lucide");
  const [query, setQuery] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = (icon: ProgramPageIcon | null) => {
    onChange(icon);
    setOpen(false);
  };
  const names = PAGE_ICON_NAMES.filter((name) => name.toLowerCase().includes(query.toLowerCase()));

  return (
    <Field label={label}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 w-full items-center gap-2 rounded-lg border border-input px-3 text-sm shadow-sm hover:bg-accent"
          >
            {value ? (
              <PageIcon icon={value} className="text-base" />
            ) : (
              <Smile className="h-4 w-4 text-muted-foreground" aria-hidden />
            )}
            <span className="truncate text-muted-foreground">
              {value ? (value.kind === "lucide" ? value.value : value.kind === "emoji" ? "Emoji" : "Custom image") : "Choose an icon"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3">
          <div className={`mb-3 flex rounded-lg border border-border p-0.5 ${lucideOnly ? "hidden" : ""}`}>
            {(["lucide", "emoji", "image"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setTab(kind)}
                className={`flex-1 rounded-md py-1 text-xs font-medium ${
                  tab === kind
                    ? "bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300"
                    : "text-muted-foreground"
                }`}
              >
                {kind === "lucide" ? "Icons" : kind === "emoji" ? "Emoji" : "Upload"}
              </button>
            ))}
          </div>

          {tab === "lucide" && (
            <>
              <Input
                autoFocus
                value={query}
                placeholder="Search icons"
                onChange={(e) => setQuery(e.target.value)}
                className="mb-2"
              />
              <div className="grid max-h-56 grid-cols-7 gap-1 overflow-y-auto">
                {names.map((name) => (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    aria-label={name}
                    onClick={() => pick({ kind: "lucide", value: name })}
                    className={`flex h-9 items-center justify-center rounded-md hover:bg-accent ${
                      value?.kind === "lucide" && value.value === name ? "bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300" : ""
                    }`}
                  >
                    <LucideByName name={name} className="h-4 w-4" />
                  </button>
                ))}
                {!names.length && (
                  <p className="col-span-7 py-4 text-center text-xs text-muted-foreground">No icons match.</p>
                )}
              </div>
            </>
          )}

          {tab === "emoji" && (
            <>
              <Input
                placeholder="Type or paste any emoji"
                maxLength={8}
                defaultValue={value?.kind === "emoji" ? value.value : ""}
                onChange={(e) => {
                  const emoji = e.target.value.trim();
                  if (emoji) onChange({ kind: "emoji", value: emoji });
                }}
                className="mb-2"
              />
              <div className="grid grid-cols-8 gap-1">
                {QUICK_EMOJI.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => pick({ kind: "emoji", value: emoji })}
                    className="flex h-9 items-center justify-center rounded-md text-lg hover:bg-accent"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </>
          )}

          {tab === "image" && (
            <div className="flex flex-col items-center gap-2 py-4">
              <button
                type="button"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Upload your own
              </button>
              <p className="text-center text-xs text-muted-foreground">
                A square PNG with a transparent background works best.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  setBusy(true);
                  const url = await upload(file);
                  setBusy(false);
                  if (url) pick({ kind: "image", value: url });
                }}
              />
            </div>
          )}

          {value && !lucideOnly && (
            <button
              type="button"
              onClick={() => pick(null)}
              className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-destructive"
            >
              Remove icon
            </button>
          )}
        </PopoverContent>
      </Popover>
    </Field>
  );
}

export function CtaField({
  value,
  onChange,
}: {
  value: ProgramPageCta | null;
  onChange: (value: ProgramPageCta | null) => void;
}) {
  const cta = value ?? { label: "", action: "join" as const, url: null };
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <ToggleField
        label="Button"
        checked={!!value}
        onChange={(on) => onChange(on ? { label: "Join the program", action: "join", url: null } : null)}
      />
      {value && (
        <>
          <TextField label="Label" value={cta.label} maxLength={40} onChange={(label) => onChange({ ...cta, label })} />
          <SegmentedField
            label="Goes to"
            value={cta.action}
            options={[
              { value: "join", label: "Your join page" },
              { value: "url", label: "A link" },
            ]}
            onChange={(action) => onChange({ ...cta, action })}
          />
          {cta.action === "url" ? (
            <TextField
              label="Link"
              value={cta.url ?? ""}
              maxLength={2048}
              placeholder="https://"
              hint="Must start with https://"
              onChange={(url) => onChange({ ...cta, url: url || null })}
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              Hidden for affiliates who have already joined.
            </p>
          )}
        </>
      )}
    </div>
  );
}
